import React, { useEffect, useRef, useState } from 'react'
import OtpInputField from './OTPInputField'
import { useApp } from '../../../context/usePortal'
import SuccessPopup from './SuccessPopup'
import ChangePhoneNumber from '../../../components/ChangePhoneNumber'
import { FaceVerifyScreens } from '../../../constants/face.const'

interface CumulativeRangePopupProps {
  pin: string
  onClose: () => void
}

const VerifyOTPAndResetPIN: React.FC<CumulativeRangePopupProps> = ({
  pin,
  onClose,
}) => {
  const { resetPIN, user, getCode } = useApp()
  const [otp, setOtp] = useState<string>('')
  const [actionButtonAction, setActionButtonAction] = useState<string>('Verify')
  const [seconds, setSeconds] = useState<number>(60)
  const [timerActive, setTimerActive] = useState<boolean>(false)
  const [message, setMessage] = useState<{ type: 'success' | 'fail'; message: string; } | undefined>(undefined)
  const [resetOtp, setResetOtp] = useState<boolean>(true)
  const [verifyOtpStatus, setVerifyOtpStatus] = useState<'success' | 'failed' | ''>('')
  const isMounted = useRef<boolean>(false)

  const phoneNumber = user?.country_code + '-' + user?.user_phone_number

  useEffect(() => {
    if (!isMounted.current) {
      sendOTP()
      isMounted.current = true
    }
  }, [])

  useEffect(() => {
    let intervalId: NodeJS.Timeout | null = null
    if (timerActive && seconds > 0) {
      intervalId = setInterval(() => {
        setSeconds((prevSeconds) => prevSeconds - 1)
      }, 1000)
    } else if (seconds <= 0) {
      setActionButtonAction('resend OTP')
    }

    return () => {
      if (intervalId) clearInterval(intervalId)
    }
  }, [timerActive, seconds])

  const handleChangeOtp = (newValue: string = '') => {
    setOtp(newValue)
  }

  const handleVerifyOtpAndResetPIN = () => {
    const action = actionButtonAction.toLowerCase().trim()
    if (action === 'send otp' || action === 'resend otp') {
      sendOTP()
    } else if (action === 'verify') {
      VerifyOTP()
    }
  }

  const sendOTP = async () => {
    setResetOtp(true)
    const data = await getCode(phoneNumber, 'reset_pin')
    if (data.isOk() && data?.response?.success) {
      setMessage({
        type: 'success',
        message: data?.response?.data?.message || 'OTP sent successfully',
      });
      setOtp('')
      setVerifyOtpStatus('')
      setActionButtonAction('verify')
      setSeconds(60)
      setTimerActive(true)
    }
    if (data.response?.success === false) {
      setMessage({
        type: 'fail',
        message: data?.response?.error?.message || 'Failed to send OTP',
      })
      setActionButtonAction('resend OTP')
    }
  }

  const VerifyOTP = async () => {
    if (otp.length !== 6) return

    setActionButtonAction('verifying')
    const { response } = await resetPIN(pin, otp, phoneNumber)

    if (response?.success) {
      setActionButtonAction('')
      setVerifyOtpStatus('success')
    } else {
      setActionButtonAction('resend OTP')
      setTimerActive(false)
      setVerifyOtpStatus('failed')
      setMessage({
        type: 'fail',
        message: response?.error?.message || 'Failed to verify OTP',
      })
    }
  }

  if (verifyOtpStatus === 'success') {
    return <SuccessPopup onClose={onClose} type="resetPIN" />
  }

  let action = actionButtonAction.toLowerCase().trim()
  let content: JSX.Element = <>{message && <p className={`text-center text-sm ${message.type === 'success' ? 'text-green-800' : 'text-red-500'}`}>{message.message}</p>}</>


  let buttonContent = <></>

  if (action === 'verifying') {
    buttonContent = (
      <div className="flex flex-col items-center justify-center">
        <span className="loader border-5 border-green-800"></span>
        <span className="mt-2 text-lg font-bold text-green-800">Verifying</span>
      </div>
    )
  } else if (action !== 'verifying') {
    buttonContent = (
      <button
        type="button"
        className={`${
          action === 'resend otp'
            ? 'text-green-800 hover:bg-green-800 hover:text-white'
            : 'bg-green-800 text-white'
        } mx-auto mt-5 block w-full rounded-3xl border-1 border-green-800 px-9 py-3 text-center font-bold capitalize`}
        onClick={handleVerifyOtpAndResetPIN}>
        {actionButtonAction}
      </button>
    )
  }

  return (
    <div>
      <h2 className="my-2 text-center text-xl font-bold">
        Mobile Phone Verification
      </h2>
      <h3 className="text-md text-center font-bold">One Time Password</h3>
      <OtpInputField
        length={6}
        onChangeOtp={handleChangeOtp}
        editable={action !== 'verify'}
        resetOtp={resetOtp}
      />
      {content}
      {buttonContent}

      <div className='mt-4 flex items-center justify-center'>
        <ChangePhoneNumber screen={FaceVerifyScreens.RESET_PIN} />
      </div>
    </div>
  )
}

export default VerifyOTPAndResetPIN
